<template>
    <div>
        <ts-page-title
            title="Loan Overdue"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Loan Overdue', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Branch / CO / Currency / Overdue bucket -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Credit Officer</label>
                            <Select
                                v-model="model.co_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                placeholder="All COs"
                            >
                                <Option
                                    v-for="item in employees"
                                    :value="item.employee_id"
                                    :key="item.employee_id"
                                    :label="item.employee_name_en"
                                >
                                    {{ item.employee_name_en }}
                                </Option>
                            </Select>
                        </div>

                        <div class="filter-cell" style="grid-column: span 2">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button
                                    v-for="c in currencies"
                                    :key="c.currency_id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                    @click="model.currency_id = c.currency_id"
                                >{{ c.currency_code }}</button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Overdue Bucket</label>
                            <div class="pill-group">
                                <button
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': !model.overdue_bucket }"
                                    @click="model.overdue_bucket = null"
                                >All</button>
                                <button
                                    v-for="b in overdueBuckets"
                                    :key="b.id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.overdue_bucket === b.id }"
                                    @click="model.overdue_bucket = b.id"
                                >
                                    <span class="dot" :style="{ background: b.dot }"></span>
                                    {{ b.label }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: Date / Search + actions -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 5">
                            <label class="filter-lbl">As of Date <span class="text-danger">*</span></label>
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <div class="pill-group">
                                    <button
                                        v-for="p in datePresets"
                                        :key="p.value"
                                        type="button"
                                        class="pill"
                                        :class="{ 'pill-active': model.date_preset === p.value }"
                                        @click="model.date_preset = p.value"
                                    >{{ p.label }}</button>
                                </div>
                                <div v-if="model.date_preset === 'custom'" class="tw-flex-1">
                                    <DatePicker
                                        type="date"
                                        format="dd-MM-yyyy"
                                        placeholder="Select date (up to yesterday)"
                                        style="width: 100%"
                                        :options="datePickerOptions"
                                        @on-change="onCustomDateChange"
                                    ></DatePicker>
                                </div>
                                <div v-else class="date-preview">{{ presetPreview }}</div>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 7">
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input
                                        type="text"
                                        class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Loan #, customer code / name, or CO name"
                                        @keyup.enter="onSearch"
                                    />
                                    <button
                                        v-if="searchQuery"
                                        type="button"
                                        class="search-clear"
                                        title="Clear search"
                                        @click.prevent="onClearSearch"
                                    ><i class="fas fa-times-circle"></i></button>
                                </div>
                                <button
                                    type="button"
                                    class="search-btn"
                                    :disabled="loading"
                                    @click.prevent="onSearch"
                                >
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button
                                    type="button"
                                    class="reset-btn"
                                    @click.prevent="onReset"
                                    title="Reset all filters"
                                ><i class="fas fa-undo"></i></button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS (4 overdue buckets) ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon">
                        <i :class="'fas ' + card.icon"></i>
                    </div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div class="summary-card__count">
                        <template v-if="summaryLoading">…</template>
                        <template v-else>{{ card.count.toLocaleString() }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ fmt(card.total) }}</span>
                        <span class="summary-card__currency">{{ currencyCode }}</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> No search yet
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No overdue loans found
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} rows
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button
                        type="button"
                        class="export-btn"
                        :disabled="exporting"
                        @click.prevent="exportExcel"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Ready when you are — click <b>Search</b> above.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No overdue loans match your filters.</div>
                    <div class="tw-text-xs tw-text-gray-400">Try a different date or clear the bucket filter.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="rows"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    show-summary
                    :summary-method="summaryMethod"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

const BUCKET_META = {
    under_30: { label: '≤ 30 days',    dot: '#10b981', tone: 'under30' },
    '30_to_90': { label: '31–90 days', dot: '#f59e0b', tone: 'mid' },
    over_90:  { label: '> 90 days',    dot: '#ef4444', tone: 'over90' }
}

export default {
    name: 'loanOverdueV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            summary: {
                all:      { count: 0, total: '0' },
                under_30: { count: 0, total: '0' },
                '30_to_90': { count: 0, total: '0' },
                over_90:  { count: 0, total: '0' }
            },
            totals: {
                count: 0,
                outstanding_amount:  '0',
                principal_overdue:   '0',
                interest_overdue:    '0',
                monthly_fee_overdue: '0',
                rescue_fee_overdue:  '0',
                total_overdue:       '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            datePresets: [
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'custom',    label: 'Custom' }
            ],
            overdueBuckets: [
                { id: 'under_30',  label: '≤ 30 days',   dot: '#10b981' },
                { id: '30_to_90',  label: '31–90 days',  dot: '#f59e0b' },
                { id: 'over_90',   label: '> 90 days',   dot: '#ef4444' }
            ],
            model: {
                branch_id: [],
                co_id: [],
                currency_id: null,
                overdue_bucket: null,
                date_preset: 'yesterday',
                as_of_date: null,
                search: null,
                sort: 'overdue_days',
                sort_dir: 'desc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    created () {
        this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id
    },
    computed: {
        ...mapState('report/resource', ['employees', 'currencies']),

        currencyCode () {
            if (!this.model.currency_id) return ''
            const c = (this.currencies || []).find(c => c.currency_id === this.model.currency_id)
            return c ? c.currency_code : ''
        },
        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },

        presetPreview () {
            const today = moment()
            switch (this.model.date_preset) {
                case 'yesterday': return today.clone().subtract(1, 'day').format('ddd, DD MMM YYYY')
                case 'custom':    return this.model.as_of_date ? this.model.as_of_date : null
            }
            return ''
        },

        activeChips () {
            const chips = []
            if (this.model.branch_id && this.model.branch_id.length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch' + (this.model.branch_id.length > 1 ? 'es' : ''),
                    tone: 'chip-branch' })
            } else {
                chips.push({ key: 'branch', icon: 'fa-building', label: 'All branches', tone: 'chip-branch' })
            }
            if (this.model.currency_id) {
                const cur = this.currencies.find(c => c.currency_id === this.model.currency_id)
                if (cur) chips.push({ key: 'currency', label: cur.currency_code, tone: 'chip-currency' })
            }
            if (this.presetPreview) {
                chips.push({ key: 'date', icon: 'fa-calendar-alt', label: this.presetPreview, tone: 'chip-date' })
            }
            if (this.model.overdue_bucket) {
                const b = BUCKET_META[this.model.overdue_bucket]
                chips.push({ key: 'bucket', icon: 'fa-clock', label: b && b.label, tone: 'chip-type' })
            }
            if (this.model.co_id && this.model.co_id.length) {
                chips.push({ key: 'co', icon: 'fa-user',
                    label: this.model.co_id.length + ' CO' + (this.model.co_id.length > 1 ? 's' : ''),
                    tone: 'chip-co' })
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                chips.push({ key: 'search', icon: 'fa-search', label: '"' + this.searchQuery.trim() + '"', tone: 'chip-search' })
            }
            return chips.filter(c => c.label)
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 601 : 471
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            const PAGINATION_ROW = 52
            return this.tableHeight + PAGINATION_ROW
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        datePickerOptions () {
            return {
                // Snapshot rows for today aren't populated until the nightly job runs,
                // so users can only pick dates up to yesterday.
                disabledDate (date) {
                    return date && date.valueOf() >= moment().startOf('day').valueOf()
                }
            }
        },

        summaryCards () {
            return [
                { key: 'all',      icon: 'fa-layer-group', tone: 'all',
                  label: 'All overdue',
                  count: this.summary.all.count, total: this.summary.all.total },
                { key: 'under_30', icon: 'fa-clock',       tone: 'under30',
                  label: '≤ 30 days',
                  count: this.summary.under_30.count, total: this.summary.under_30.total },
                { key: '30_to_90', icon: 'fa-hourglass-half', tone: 'mid',
                  label: '31–90 days',
                  count: this.summary['30_to_90'].count, total: this.summary['30_to_90'].total },
                { key: 'over_90',  icon: 'fa-exclamation-triangle', tone: 'over90',
                  label: '> 90 days',
                  count: this.summary.over_90.count, total: this.summary.over_90.total }
            ]
        },

        columns () {
            return [
                {
                    title: 'Branch',
                    key: 'branch_code',
                    width: 100,
                    fixed: 'left',
                    align: 'center',
                    sortable: 'custom',
                    render: (h, params) =>
                        h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                },
                {
                    title: 'Loan #',
                    key: 'loan_account_no',
                    width: 170,
                    fixed: 'left',
                    align: 'center',
                    sortable: 'custom',
                    render: (h, params) => {
                        const val = params.row.loan_account_no || ''
                        return h('span', {
                            class: 'loan-copy',
                            attrs: { title: 'Click to copy ' + val },
                            on: { click: () => this.copyToClipboard(val) }
                        }, [
                            h('code', { class: 'tw-text-xs' }, val),
                            h('i', { class: 'far fa-copy loan-copy__icon' })
                        ])
                    }
                },
                {
                    title: 'Customer',
                    key: 'customer_name_en',
                    minWidth: 240,
                    fixed: 'left',
                    sortable: 'custom',
                    render: (h, params) => h('div', { class: 'code-name-cell' }, [
                        h('span', { class: 'code-chip' }, params.row.customer_code || '—'),
                        h('span', { class: 'name-text' }, ' ' +
                            (this.isKh
                                ? (params.row.customer_name_kh || params.row.customer_name_en)
                                : (params.row.customer_name_en || params.row.customer_name_kh)))
                    ])
                },
                {
                    title: 'Disbursed',
                    key: 'disbursement_date',
                    width: 115,
                    align: 'center',
                    render: (h, params) => {
                        const d = params.row.disbursement_date
                        if (!d) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', { class: 'tw-text-xs' }, moment(d).format('DD MMM YYYY'))
                    }
                },
                {
                    title: 'Overdue Days',
                    key: 'overdue_days',
                    width: 130,
                    align: 'center',
                    sortable: 'custom',
                    render: (h, params) => {
                        const days = parseInt(params.row.overdue_days, 10) || 0
                        const bucket = params.row.overdue_bucket
                        const meta = BUCKET_META[bucket] || { dot: '#94a3b8' }
                        return h('span', {
                            class: 'bucket-chip',
                            style: {
                                color: meta.dot,
                                background: this.bucketBg(bucket),
                                borderColor: this.bucketBorder(bucket)
                            }
                        }, [
                            h('span', { class: 'type-dot', style: { background: meta.dot } }),
                            days.toLocaleString() + ' d'
                        ])
                    }
                },
                {
                    title: 'Outstanding',
                    key: 'outstanding_amount',
                    width: 135,
                    align: 'right',
                    sortable: 'custom',
                    render: (h, params) => h('span', { class: 'tw-font-semibold tw-text-gray-800' },
                        this.fmt(params.row.outstanding_amount))
                },
                { title: 'Principal',    key: 'principal_overdue',   width: 130, align: 'right', sortable: 'custom', render: this.moneyCell('principal_overdue') },
                { title: 'Interest',     key: 'interest_overdue',    width: 120, align: 'right', render: this.moneyCell('interest_overdue') },
                { title: 'Home Col Fee', key: 'monthly_fee_overdue', width: 130, align: 'right', render: this.moneyCell('monthly_fee_overdue') },
                { title: 'Rescue Fee',   key: 'rescue_fee_overdue',  width: 120, align: 'right', render: this.moneyCell('rescue_fee_overdue') },
                {
                    title: 'Total Overdue',
                    key: 'total_overdue',
                    width: 140,
                    align: 'right',
                    render: (h, params) => h('span', { class: 'tw-font-semibold tw-text-red-700' },
                        this.fmt(params.row.total_overdue))
                },
                {
                    title: 'Village / Phone',
                    key: 'village_phone',
                    minWidth: 180,
                    render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                        params.row.village_phone || '—')
                },
                {
                    title: 'CO',
                    key: 'card_id',
                    minWidth: 180,
                    sortable: 'custom',
                    render: (h, params) => h('div', { class: 'code-name-cell' }, [
                        h('span', { class: 'code-chip' }, params.row.card_id || '—'),
                        h('span', { class: 'name-text' }, ' ' +
                            (this.isKh
                                ? (params.row.co_name_kh || params.row.co_name_en)
                                : (params.row.co_name_en || params.row.co_name_kh)))
                    ])
                },
                {
                    title: 'Date Meet',
                    key: 'date_meet',
                    width: 120,
                    align: 'center',
                    render: (h, params) => {
                        const d = params.row.date_meet
                        if (!d) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', { class: 'tw-text-xs' }, moment(d).format('DD MMM YYYY'))
                    }
                },
                {
                    title: 'Reason Late',
                    key: 'reason_late_en',
                    minWidth: 220,
                    render: (h, params) => {
                        const val = this.isKh
                            ? (params.row.reason_late_kh || params.row.reason_late_en)
                            : (params.row.reason_late_en || params.row.reason_late_kh)
                        if (!val) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', { class: 'tw-text-xs tw-text-gray-700' }, val)
                    }
                }
            ]
        }
    },
    methods: {
        ...mapActions('report/resource', ['getEmployee', 'getCurrency']),

        bucketBg (key) {
            const map = { under_30: '#ecfdf5', '30_to_90': '#fef3c7', over_90: '#fee2e2' }
            return map[key] || '#f3f4f6'
        },
        bucketBorder (key) {
            const map = { under_30: '#d1fae5', '30_to_90': '#fde68a', over_90: '#fecaca' }
            return map[key] || '#e5e7eb'
        },

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        moneyCell (key) {
            return (h, params) => {
                const val = params.row[key]
                const n = parseFloat(val)
                if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
                return h('span', this.fmt(val))
            }
        },

        summaryMethod ({ columns }) {
            const t = this.totals
            const sums = {}
            const money = k => this.fmtStrict(t[k])

            columns.forEach((col, idx) => {
                if (idx === 0) {
                    sums[col.key] = { key: col.key, value: 'TOTAL' }
                    return
                }
                let value = ''
                switch (col.key) {
                    case 'outstanding_amount':  value = money('outstanding_amount'); break
                    case 'principal_overdue':   value = money('principal_overdue'); break
                    case 'interest_overdue':    value = money('interest_overdue'); break
                    case 'monthly_fee_overdue': value = money('monthly_fee_overdue'); break
                    case 'rescue_fee_overdue':  value = money('rescue_fee_overdue'); break
                    case 'total_overdue':       value = money('total_overdue'); break
                    default:                    value = ''
                }
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },

        onCustomDateChange (val) {
            this.model.as_of_date = val || null
        },

        buildParams () {
            const p = {
                branch_id:      this.model.branch_id,
                co_id:          this.model.co_id,
                currency_id:    this.model.currency_id,
                overdue_bucket: this.model.overdue_bucket,
                date_preset:    this.model.date_preset,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if (this.model.date_preset === 'custom') {
                p.as_of_date = this.model.as_of_date
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                p.search = this.searchQuery.trim()
            }
            return p
        },

        fetchData () {
            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/loanOverdueV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null
                    const totals  = response.totals  || null

                    this.rows = data
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }

                    if (summary) {
                        const empty = { count: 0, total: '0' }
                        this.summary = {
                            all:        Object.assign({}, empty, summary.all        || {}),
                            under_30:   Object.assign({}, empty, summary.under_30   || {}),
                            '30_to_90': Object.assign({}, empty, summary['30_to_90'] || {}),
                            over_90:    Object.assign({}, empty, summary.over_90    || {})
                        }
                    }
                    if (totals) {
                        this.totals = Object.assign(
                            { count: 0, outstanding_amount: '0', principal_overdue: '0',
                              interest_overdue: '0', monthly_fee_overdue: '0',
                              rescue_fee_overdue: '0', total_overdue: '0' },
                            totals
                        )
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onSearch () {
            if (this.model.date_preset === 'custom' && !this.model.as_of_date) {
                this.notice({ type: 'warning', text: 'Please pick a date before searching.' })
                return
            }
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const hadSearch = !!(this.searchQuery && this.searchQuery.trim())
            this.searchQuery = ''
            if (hadSearch && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.co_id = []
            this.model.overdue_bucket = null
            this.model.date_preset = 'yesterday'
            this.model.as_of_date = null
            this.model.sort = 'overdue_days'
            this.model.sort_dir = 'desc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = 'overdue_days'
                this.model.sort_dir = 'desc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/loanOverdueV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        copyToClipboard (text) {
            if (!text) return
            const done = () => {
                this.$Message && this.$Message.success
                    ? this.$Message.success('Copied: ' + text)
                    : this.notice({ type: 'success', text: 'Copied: ' + text })
            }
            const fail = () => this.notice({ type: 'error', text: 'Copy failed' })

            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(done).catch(fail)
                return
            }
            try {
                const ta = document.createElement('textarea')
                ta.value = text
                ta.style.position = 'fixed'
                ta.style.opacity = '0'
                document.body.appendChild(ta)
                ta.select()
                document.execCommand('copy')
                document.body.removeChild(ta)
                done()
            } catch (e) {
                fail()
            }
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'LOAN OVERDUE',
                desc: not.text
            })
        }
    },
    watch: {
        'model.branch_id': debounce(function (value) {
            this.model.co_id = []
            this.getEmployee({ branch_id: value })
        }, 500),
        'model.overdue_bucket' () {
            // Re-fetch page 1 when bucket changes and we already have a search.
            if (this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        }
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.getCurrency() })
    }
}
</script>

<style scoped>
/* ── Filter grid ───────────────────────────────────────────────── */
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    margin-bottom: 4px;
    height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }
::v-deep .ts-form-btn { height: 34px; }

/* ── Pill selector ─────────────────────────────────────────────── */
.pill-group {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 3px;
    padding: 3px;
    background: #f3f4f6;
    border-radius: 6px;
    height: 34px;
    box-sizing: border-box;
    overflow-x: auto;
    max-width: 100%;
}
.pill {
    background: transparent;
    border: none;
    padding: 4px 10px;
    font-size: 12px;
    color: #4b5563;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1.2;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff;
    color: #111827;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
.pill .dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 5px;
}
.date-preview {
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
    padding-left: 8px;
    line-height: 34px;
    white-space: nowrap;
}

/* ── Chip pills ──────────────────────────────────────────────── */
.chip-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    font-size: 11px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #374151;
    line-height: 1.5;
    white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-branch   { background: #eef2ff; color: #4338ca; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-date     { background: #fef3c7; color: #92400e; }
.chip-type     { background: #fee2e2; color: #991b1b; }
.chip-co       { background: #f3e8ff; color: #6b21a8; }
.chip-search   { background: #fce7f3; color: #9d174d; }

@media (max-width: 991px) {
    .filter-grid > .filter-cell { grid-column: span 12 !important; }
}

/* ── Search group ─────────────────────────────────────────────── */
.search-group {
    display: flex;
    align-items: stretch;
    height: 34px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.15s, box-shadow 0.15s;
}
.search-group:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.search-input-wrap {
    position: relative;
    flex: 1;
    display: flex;
    align-items: stretch;
    min-width: 0;
}
.search-input {
    flex: 1;
    border: none !important;
    height: 100% !important;
    padding: 0 32px 0 12px;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #111827;
    min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 4px;
    font-size: 14px;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: color 0.12s, background 0.12s;
}
.search-clear:hover { color: #4b5563; background: #f3f4f6; }
.search-btn {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: background 0.15s;
    white-space: nowrap;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb;
    color: #6b7280;
    border: none;
    border-left: 1px solid #e5e7eb;
    padding: 0 12px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

/* ── Results card ─────────────────────────────────────────────── */
.results-card {
    background: #ffffff;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}
.results-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #ffffff;
    gap: 8px;
    flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    gap: 8px;
    min-height: 52px;
}
.pagination-left {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 28px;
    line-height: 28px;
    font-size: 13px;
    color: #6b7280;
}
.pagination-lbl {
    display: inline-flex;
    align-items: center;
    height: 28px;
    line-height: 28px;
    white-space: nowrap;
}
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }
.pagination-left ::v-deep .ivu-select-small { display: inline-flex; align-items: center; }
.pagination-left ::v-deep .ivu-select-small .ivu-select-selection {
    display: flex; align-items: center;
}
.pagination-left ::v-deep .ivu-select-small .ivu-icon {
    top: 50% !important;
    transform: translateY(-50%);
    margin-top: 0 !important;
}

/* ── Summary cards (4 overdue buckets) ─────────────────────────── */
.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-top: 3px solid;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 110px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-size: 11px;
    flex-shrink: 0;
}
.summary-card__label {
    flex: 1;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    padding: 1px 6px;
    background: #f3f4f6;
    border-radius: 10px;
    line-height: 1.4;
}
.summary-card__value {
    display: flex;
    align-items: baseline;
    gap: 6px;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
}
.summary-card__amount {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}
.summary-card__currency {
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.03em;
    flex-shrink: 0;
}
.summary-card--all     { border-top-color: #6366f1; }
.summary-card--all     .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--under30 { border-top-color: #10b981; }
.summary-card--under30 .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--mid     { border-top-color: #f59e0b; }
.summary-card--mid     .summary-card__icon { background: #fef3c7; color: #92400e; }
.summary-card--over90  { border-top-color: #ef4444; }
.summary-card--over90  .summary-card__icon { background: #fee2e2; color: #991b1b; }

/* ── Table header + footer ────────────────────────────────────── */
::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px;
    color: #374151;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important;
    font-weight: 700 !important;
    color: #111827 !important;
    border-top: 2px solid #d1d5db !important;
}
::v-deep .ivu-table-summary td .ivu-table-cell { font-size: 12px; }
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

/* ── Export controls ──────────────────────────────────────────── */
.export-group {
    display: inline-flex;
    align-items: stretch;
    height: 32px;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db;
    background: #ffffff;
}
.export-select {
    border: 0;
    border-right: 1px solid #d1d5db;
    background: #ffffff;
    padding: 0 28px 0 12px;
    font-size: 12px;
    color: #374151;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    height: 100%;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 10px 6px;
}
.export-select:hover { background-color: #f9fafb; }
.export-btn {
    background: #10b981;
    color: white;
    border: none;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: background 0.15s;
    white-space: nowrap;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    height: 100%;
    position: absolute;
    inset: 0;
}
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx. -->
<style>
.code-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
}
.code-chip {
    display: inline-block;
    min-width: 68px;
    padding: 0 4px;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    text-align: center;
    letter-spacing: 0.02em;
    line-height: 18px;
    white-space: nowrap;
    flex-shrink: 0;
}
.name-text {
    color: #111827;
    font-size: 12px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}
.bucket-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0 8px;
    height: 20px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 10px;
    border: 1px solid;
    letter-spacing: 0.02em;
    white-space: nowrap;
}
.type-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}
.loan-copy {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 4px;
    transition: background 0.12s;
}
.loan-copy:hover { background: #eff6ff; }
.loan-copy:active { background: #dbeafe; }
.loan-copy__icon {
    font-size: 10px;
    color: #9ca3af;
    opacity: 0;
    transition: opacity 0.12s;
}
.loan-copy:hover .loan-copy__icon { opacity: 1; }
.branch-code {
    display: inline-block;
    padding: 1px 6px;
    font-size: 11px;
    font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #4338ca;
    background: #eef2ff;
    border: 1px solid #e0e7ff;
    border-radius: 3px;
    letter-spacing: 0.02em;
}
</style>
